import { createContext, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

type Lang = 'ru' | 'en';

interface LanguageContextType {
  lang: Lang;
  setLanguage: (lang: Lang) => void;
  localizedPath: (path: string) => string;
  /** Strip language prefix from a path, e.g. /en/dashboard → /dashboard */
  stripLangPrefix: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const lang: Lang = location.pathname.startsWith('/en/') || location.pathname === '/en' ? 'en' : 'ru';

  // Sync i18n language with URL
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }

  const localizedPath = useCallback(
    (path: string): string => {
      // Remove any existing /en prefix
      const cleanPath = path.startsWith('/en/') ? path.slice(3) : path.startsWith('/en') ? path.slice(3) || '/' : path;
      if (lang === 'en') {
        return `/en${cleanPath}`;
      }
      return cleanPath;
    },
    [lang]
  );

  const stripLangPrefix = useCallback((path: string): string => {
    if (path.startsWith('/en/')) return path.slice(3);
    if (path === '/en') return '/';
    return path;
  }, []);

  const setLanguage = useCallback(
    (newLang: Lang) => {
      localStorage.setItem('creator-lang', newLang);
      i18n.changeLanguage(newLang);
      const currentPath = stripLangPrefix(location.pathname);
      const newPath = newLang === 'en' ? `/en${currentPath}` : currentPath;
      navigate(newPath + location.search, { replace: true });
    },
    [i18n, location.pathname, location.search, navigate, stripLangPrefix]
  );

  const value = useMemo(
    () => ({ lang, setLanguage, localizedPath, stripLangPrefix }),
    [lang, setLanguage, localizedPath, stripLangPrefix]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
